import { useEffect, useState } from 'react';
import { NavLink, Link, useNavigate } from 'react-router-dom';

import { useBillingInfo } from '../hooks/useBillingInfo';
import { usePermissions } from '../hooks/usePermissions';
import { api } from '../services/api';
import { useAuth } from '../stores/auth';
import { useI18n } from '../i18n/provider';
import { LocaleSwitcher } from './LocaleSwitcher';
import { NotificationBell } from './NotificationBell';

export function Header(): JSX.Element {
  const { logout, user } = useAuth();
  const { t } = useI18n();
  const navigate = useNavigate();
  const apiBase = import.meta.env?.VITE_PUBLIC_API_BASE_URL || 'http://localhost:4000';
  const envBadge = apiBase.includes('localhost')
    ? 'Local'
    : apiBase.includes('api.walletgate.app')
      ? 'Production'
      : 'Staging';
  const envBadgeLabel = envBadge === 'Local'
    ? t('header.envLocal')
    : envBadge === 'Production'
      ? t('header.envProduction')
      : t('header.envStaging');
  const [open, setOpen] = useState(false);
  const [accountOpen, setAccountOpen] = useState(false);
  const [isHeaderScrolled, setIsHeaderScrolled] = useState(false);
  const [usage, setUsage] = useState<{ used: number; limit: number } | null>(null);
  const [hasAnomaly, setHasAnomaly] = useState<boolean>(false);
  const [hasWebhooks, setHasWebhooks] = useState<boolean>(false);
  const [hasTeam, setHasTeam] = useState<boolean>(false);
  const [isOwner, setIsOwner] = useState<boolean>(false);
  const showAdminTools = !!user?.isSuperAdmin || isOwner;
  const perms = usePermissions();

  const { data: billingData } = useBillingInfo({ pollIntervalMs: 60_000 });

  useEffect(() => {
    if (!billingData) return;
    const used = billingData.data.currentMonth?.total || 0;
    const limit = billingData.data.plan?.limits?.monthlyVerifications || 1000;
    setUsage({ used, limit });
    const planHasAnomaly = !!billingData.data?.plan?.features?.hasAnomalyDetection;
    const planHasWebhooks = !!billingData.data?.plan?.features?.hasWebhooks;
    const planHasTeam = !!billingData.data?.plan?.features?.hasTeamManagement;
    // Super admins bypass plan gating; always show nav for these features
    setHasAnomaly(user?.isSuperAdmin ? true : planHasAnomaly);
    setHasWebhooks(user?.isSuperAdmin ? true : planHasWebhooks);
    setHasTeam(user?.isSuperAdmin ? true : planHasTeam);
  }, [billingData, user?.isSuperAdmin]);

  useEffect(() => {
    if (!user) return;
    let mounted = true;
    void (async () => {
      try {
        const res = await api.ownerMe();
        if (mounted) setIsOwner(!!res.isOwner);
      } catch {
        if (mounted) setIsOwner(false);
      }
    })();
    return () => {
      mounted = false;
    };
  }, [user?.email]);

  useEffect(() => {
    const handleScroll = () => setIsHeaderScrolled(window.scrollY > 6);
    handleScroll();
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  async function handleLogout(): Promise<void> {
    await logout();
    navigate('/login');
  }

  const desktopNavBaseClass =
    'px-4 py-2 rounded-lg text-sm transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-400 focus-visible:ring-offset-1 focus-visible:ring-offset-slate-50';
  const desktopNavActiveClass = 'bg-white text-slate-950 font-semibold shadow-sm ring-1 ring-slate-300/80';
  const desktopNavInactiveClass = 'text-slate-600 font-medium hover:text-slate-900 hover:bg-white/70';

  return (
    <header
      className={`bg-white fixed top-0 left-0 right-0 z-50 transition-shadow duration-200 ${
        isHeaderScrolled ? 'shadow-[0_6px_18px_rgba(15,23,42,0.08)]' : ''
      }`}
    >
      <div className="border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3">
          {/* Top Row - Brand and User Actions */}
          <div className="flex min-h-12 items-center justify-between gap-4">
          <div className="flex min-w-0 items-center gap-2 sm:gap-3">
            <img
              src="/logo.svg"
              alt="WalletGate"
              className="w-7 h-7 sm:w-8 sm:h-8"
            />
            <div className="text-lg sm:text-xl font-brand font-semibold tracking-tight text-slate-900">
              WalletGate
            </div>
            {envBadge !== 'Production' && (
              <span
                className={`hidden sm:inline-flex text-xs px-2.5 py-1 rounded-full font-medium border ${envBadge === 'Local' ? 'bg-yellow-50 text-yellow-700 border-yellow-200' : 'bg-blue-50 text-blue-700 border-blue-200'}`}
              >
                {envBadgeLabel}
              </span>
            )}
          </div>
          <div className="flex min-w-0 items-center justify-end gap-2 sm:gap-3 lg:gap-4">
            {/* Hide usage meter for staff/owners - they have unlimited access */}
            {usage && !user?.isSuperAdmin && !isOwner && (
              <div className="hidden lg:flex h-10 items-center gap-2 bg-slate-100 border border-slate-200 rounded-xl px-3">
                <div className="flex items-center space-x-2">
                  <div className="w-16 h-1.5 bg-slate-200 rounded-full">
                    <div
                      className="h-1.5 bg-blue-500 rounded-full"
                      style={{
                        width: `${Math.min(100, Math.round((usage.used / Math.max(1, usage.limit)) * 100))}%`,
                      }}
                    />
                  </div>
                  <div className="text-xs font-medium text-slate-700">
                    {usage.used}/{usage.limit}
                  </div>
                </div>
              </div>
            )}
            <LocaleSwitcher compact className="hidden md:block [&_select]:h-10 [&_select]:py-0 [&_select]:text-sm [&_select]:leading-10 [&_select]:pl-3 [&_select]:pr-8" />
            {/* Notification Bell - visible on all screen sizes, right before account */}
            <NotificationBell />
            {/* Account Dropdown */}
            <div className="relative hidden md:block">
              <button
                onClick={() => setAccountOpen(!accountOpen)}
                className="flex h-10 items-center gap-2 text-sm text-slate-600 bg-slate-100 border border-slate-200 px-3 rounded-xl hover:bg-slate-200/60 transition-colors"
              >
                <span className="truncate max-w-[140px]">{user?.email}</span>
                {user?.isSuperAdmin && (
                  <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-slate-200 text-slate-600">
                    {t('header.staff')}
                  </span>
                )}
                <svg className={`w-4 h-4 transition-transform ${accountOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
              {accountOpen && (
                <>
                  <div className="fixed inset-0 z-40" onClick={() => setAccountOpen(false)} />
                  <div className="absolute right-0 mt-2 w-48 bg-white rounded-xl shadow-lg border border-slate-100 py-1 z-50">
                    <div className="px-3 py-2 border-b border-slate-100">
                      <div className="text-xs text-slate-500">{t('header.signedInAs')}</div>
                      <div className="text-sm font-medium text-slate-900 truncate">{user?.email}</div>
                    </div>
                    <Link
                      to="/admin/billing"
                      onClick={() => setAccountOpen(false)}
                      className="flex items-center gap-2 px-3 py-2 text-sm text-slate-700 hover:bg-slate-50"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
                      </svg>
                      {t('common.billing')}
                    </Link>
                    {showAdminTools && (
                      <>
                        <div className="mx-3 my-1 border-t border-slate-100" />
                        <div className="px-3 py-1 text-[11px] uppercase tracking-wide text-slate-400">{t('header.adminTools')}</div>
                        {user?.isSuperAdmin && (
                          <Link
                            to="/admin/superadmin"
                            onClick={() => setAccountOpen(false)}
                            className="flex items-center gap-2 px-3 py-2 text-sm text-slate-700 hover:bg-slate-50"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c1.657 0 3-1.343 3-3S13.657 2 12 2 9 3.343 9 5s1.343 3 3 3zm0 2c-2.761 0-5 2.239-5 5v3h10v-3c0-2.761-2.239-5-5-5z" />
                            </svg>
                            {t('header.superAdmin')}
                          </Link>
                        )}
                        <Link
                          to="/admin/blog"
                          onClick={() => setAccountOpen(false)}
                          className="flex items-center gap-2 px-3 py-2 text-sm text-slate-700 hover:bg-slate-50"
                        >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 20H5a2 2 0 01-2-2V6a2 2 0 012-2h10a2 2 0 012 2v1m2 13a2 2 0 01-2-2V7m2 13a2 2 0 002-2V9a2 2 0 00-2-2h-2m-4-3H9M7 16h6M7 8h6v4H7V8z" />
                            </svg>
                          {t('header.blogManager')}
                        </Link>
                        {isOwner && (
                          <Link
                            to="/admin/owner"
                            onClick={() => setAccountOpen(false)}
                            className="flex items-center gap-2 px-3 py-2 text-sm text-slate-700 hover:bg-slate-50"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4a4 4 0 00-4 4v3H7a3 3 0 00-3 3v6h16v-6a3 3 0 00-3-3h-1V8a4 4 0 00-4-4z" />
                            </svg>
                            {t('header.ownerTools')}
                          </Link>
                        )}
                      </>
                    )}
                    <button
                      onClick={() => {
                        setAccountOpen(false);
                        void handleLogout();
                      }}
                      className="flex items-center gap-2 w-full px-3 py-2 text-sm text-slate-700 hover:bg-slate-50"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                      </svg>
                      {t('common.signOut')}
                    </button>
                  </div>
                </>
              )}
            </div>
            {/* Hamburger Menu - Mobile Only */}
            <button
              aria-label={t('header.toggleMenu')}
              className="md:hidden p-2 rounded-md hover:bg-slate-100 transition-colors"
              onClick={() => setOpen(!open)}
            >
              <svg
                className="w-6 h-6 text-slate-700"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                {open ? (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                ) : (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                )}
              </svg>
            </button>
          </div>
          </div>
        </div>
      </div>

      {/* Sub-navigation */}
      <div className="bg-slate-50/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <nav className="hidden md:flex flex-wrap items-center justify-center gap-2 py-3">
            <NavLink
              to="/admin"
              end
              className={({ isActive }) =>
                `${desktopNavBaseClass} ${isActive ? desktopNavActiveClass : desktopNavInactiveClass}`
              }
            >
              {t('common.dashboard')}
            </NavLink>
            <NavLink
              to="/admin/keys"
              data-testid="nav-keys"
              className={({ isActive }) =>
                `${desktopNavBaseClass} ${isActive ? desktopNavActiveClass : desktopNavInactiveClass}`
              }
            >
              {t('common.apiKeys')}
            </NavLink>
            {perms.canUsePlayground && (
              <NavLink
                to="/admin/playground"
                data-testid="nav-playground"
                className={({ isActive }) =>
                  `${desktopNavBaseClass} ${isActive ? desktopNavActiveClass : desktopNavInactiveClass}`
                }
              >
                {t('common.playground')}
              </NavLink>
            )}
            {(hasTeam || perms.isTeamMember) && (
              <NavLink
                to="/admin/team"
                data-testid="nav-team"
                className={({ isActive }) =>
                  `${desktopNavBaseClass} ${isActive ? desktopNavActiveClass : desktopNavInactiveClass}`
                }
              >
                {t('common.team')}
              </NavLink>
            )}
            {hasWebhooks && (
              <NavLink
                to="/admin/webhooks"
                data-testid="nav-webhooks"
                className={({ isActive}) =>
                  `${desktopNavBaseClass} ${isActive ? desktopNavActiveClass : desktopNavInactiveClass}`
                }
              >
                {t('common.webhooks')}
              </NavLink>
            )}
            {hasAnomaly && (
              <NavLink
                to="/admin/security"
                data-testid="nav-security"
                className={({ isActive}) =>
                  `${desktopNavBaseClass} ${isActive ? desktopNavActiveClass : desktopNavInactiveClass}`
                }
              >
                {t('common.security')}
              </NavLink>
            )}
            <NavLink
              to="/admin/audit-log"
              data-testid="nav-audit-log"
              className={({ isActive }) =>
                `${desktopNavBaseClass} ${isActive ? desktopNavActiveClass : desktopNavInactiveClass}`
              }
            >
              {t('common.auditLog')}
            </NavLink>
            <a
              href="https://docs.walletgate.app"
              target="_blank"
              rel="noopener noreferrer"
              className={`${desktopNavBaseClass} ${desktopNavInactiveClass} flex items-center gap-1`}
            >
              {t('common.docs')}
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
              </svg>
            </a>
          </nav>
        </div>
      </div>
      {open && (
        <div className="md:hidden fixed inset-0 z-[60] bg-white">
          {/* Mobile drawer header */}
          <div className="flex items-center justify-between px-4 py-3 border-b">
            <div className="text-slate-900 text-lg font-bold">WalletGate</div>
            <button aria-label={t('header.closeMenu')} onClick={() => setOpen(false)} className="p-2 rounded-md hover:bg-slate-100">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          {/* Mobile nav list */}
          <nav className="overflow-y-auto h-[calc(100vh-56px)] divide-y divide-slate-200">
            <NavLink
              to="/admin"
              end
              className={({ isActive }) =>
                `block py-3 px-6 text-sm ${isActive ? 'bg-slate-100 text-slate-900 font-semibold' : 'text-slate-800 hover:bg-slate-50'}`
              }
              onClick={() => setOpen(false)}
            >
              {t('common.dashboard')}
            </NavLink>
            <NavLink
              to="/admin/keys"
              data-testid="nav-keys"
              className={({ isActive }) =>
                `block py-3 px-6 text-sm ${isActive ? 'bg-slate-100 text-slate-900 font-semibold' : 'text-slate-800 hover:bg-slate-50'}`
              }
              onClick={() => setOpen(false)}
            >
              {t('common.apiKeys')}
            </NavLink>
            {perms.canUsePlayground && (
              <NavLink
                to="/admin/playground"
                data-testid="nav-playground"
                className={({ isActive }) =>
                  `block py-3 px-6 text-sm ${isActive ? 'bg-slate-100 text-slate-900 font-semibold' : 'text-slate-800 hover:bg-slate-50'}`
                }
                onClick={() => setOpen(false)}
              >
                {t('common.playground')}
              </NavLink>
            )}
            {(hasTeam || perms.isTeamMember) && (
              <NavLink
                to="/admin/team"
                data-testid="nav-team"
                className={({ isActive }) =>
                  `block py-3 px-6 text-sm ${isActive ? 'bg-slate-100 text-slate-900 font-semibold' : 'text-slate-800 hover:bg-slate-50'}`
                }
                onClick={() => setOpen(false)}
              >
                {t('common.team')}
              </NavLink>
            )}
            {hasWebhooks && (
              <NavLink
                to="/admin/webhooks"
                data-testid="nav-webhooks"
                className={({ isActive }) =>
                  `block py-3 px-6 text-sm ${isActive ? 'bg-slate-100 text-slate-900 font-semibold' : 'text-slate-800 hover:bg-slate-50'}`
                }
                onClick={() => setOpen(false)}
              >
                {t('common.webhooks')}
              </NavLink>
            )}
            {hasAnomaly && (
              <NavLink
                to="/admin/security"
                data-testid="nav-security"
                className={({ isActive }) =>
                  `block py-3 px-6 text-sm ${isActive ? 'bg-slate-100 text-slate-900 font-semibold' : 'text-slate-800 hover:bg-slate-50'}`
                }
                onClick={() => setOpen(false)}
              >
                {t('common.security')}
              </NavLink>
            )}
            <NavLink
              to="/admin/audit-log"
              data-testid="nav-audit-log"
              className={({ isActive }) =>
                `block py-3 px-6 text-sm ${isActive ? 'bg-slate-100 text-slate-900 font-semibold' : 'text-slate-800 hover:bg-slate-50'}`
              }
              onClick={() => setOpen(false)}
            >
              {t('common.auditLog')}
            </NavLink>
            <a
              href="https://docs.walletgate.app"
              target="_blank"
              rel="noopener noreferrer"
              className="py-3 px-6 text-sm text-slate-800 hover:bg-slate-50 flex items-center gap-1"
              onClick={() => setOpen(false)}
            >
              {t('common.docs')}
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
              </svg>
            </a>

            {/* Account Section */}
            <div className="border-t border-slate-100 mt-2 pt-2">
              <NavLink
                to="/admin/billing"
                className={({ isActive }) =>
                  `block py-3 px-6 text-sm ${isActive ? 'bg-slate-100 text-slate-900 font-semibold' : 'text-slate-800 hover:bg-slate-50'}`
                }
                onClick={() => setOpen(false)}
              >
                <span className="flex items-center gap-2">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
                  </svg>
                  {t('common.billing')}
                </span>
              </NavLink>
              {showAdminTools && (
                <>
                  <div className="px-6 pt-3 text-[11px] uppercase tracking-wide text-slate-400">{t('header.adminTools')}</div>
                  {user?.isSuperAdmin && (
                    <NavLink
                      to="/admin/superadmin"
                      className={({ isActive }) =>
                        `block py-2.5 px-6 text-sm ${isActive ? 'bg-slate-100 text-slate-900 font-semibold' : 'text-slate-800 hover:bg-slate-50'}`
                      }
                      onClick={() => setOpen(false)}
                    >
                      {t('header.superAdmin')}
                    </NavLink>
                  )}
                  <NavLink
                    to="/admin/blog"
                    className={({ isActive }) =>
                      `block py-2.5 px-6 text-sm ${isActive ? 'bg-slate-100 text-slate-900 font-semibold' : 'text-slate-800 hover:bg-slate-50'}`
                    }
                    onClick={() => setOpen(false)}
                  >
                    {t('header.blogManager')}
                  </NavLink>
                  {isOwner && (
                    <NavLink
                      to="/admin/owner"
                      className={({ isActive }) =>
                        `block py-2.5 px-6 text-sm ${isActive ? 'bg-slate-100 text-slate-900 font-semibold' : 'text-slate-800 hover:bg-slate-50'}`
                      }
                      onClick={() => setOpen(false)}
                    >
                      {t('header.ownerTools')}
                    </NavLink>
                  )}
                </>
              )}
            </div>

            {/* Prominent Logout button and email */}
            <div className="mt-3 px-6 pb-6">
              <button
                onClick={() => {
                  void handleLogout();
                }}
                className="w-full inline-flex items-center justify-center rounded-lg bg-slate-800 text-white text-sm font-semibold py-3 hover:bg-slate-900 active:bg-slate-950 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-slate-800"
              >
                {t('header.logout')}
              </button>
              <div className="mt-3 text-center text-xs text-slate-500 break-all">
                {user?.email}
              </div>
              <div className="mt-3 flex justify-center">
                <LocaleSwitcher compact />
              </div>
            </div>
          </nav>
        </div>
      )}
    </header>
  );
}
